import { ChangeEvent, useRef } from "react"

import { CellRendererProps, useCell } from "components/Bable"
import { css, cx } from "@emotion/css"

import { ReactComponent as CheckIcon } from "assets/check.svg"
import { ColorMap } from "utils/color"
import { DatePicker } from "@material-ui/pickers"
import { ReactComponent as Deactivate } from "assets/icon-deactivate.svg"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import GhostTextInput from "ui/inputs/GhostTextInput"
import MoreActions from "components/MoreActions"
import Options from "components/inputs/Options"
import { Status } from "components/Status"
import { Tax } from "models/taxes"
import { TextInput } from "components/inputs/TextInput"
import { formatFloatingPointNumber } from "utils/number-format"
import { startOfDay } from "date-fns"
import { useAuthenticated } from "providers/A12n"
import { useContextOptions } from "."
import { useI18n } from "providers/I18n"
import { useRifm } from "rifm"
import { useRow } from "components/Bable/data/useRow"
import { zonedTimeToUtc } from "date-fns-tz"

const EditableNumberCell = (props: CellRendererProps) => {
  const { dataKey, rowId } = props
  const { onUpdate } = useContextOptions()
  const [value, setValue, error] = useCell<Tax>(rowId, dataKey as keyof Tax, "")
  const row = useRow<Tax>(rowId)
  const { hasAuthority } = useAuthenticated()
  let old = useRef(value)
  const { status } = row.value ?? {}

  const onChange = (value: string) => {
    setValue(value || undefined)
  }

  const onBlur = () => {
    const hasValueDiff = old.current !== value
    if (dataKey !== undefined && hasValueDiff) {
      onUpdate(row.value, dataKey, value).then(() => (old.current = value))
    }
  }

  const numeric_rifm = useRifm({
    value: !!value || value === 0 ? `${value}` : "",
    accept: /[\d.]/g,
    onChange: (v) => onChange(v),
    format: (v) => (!!v ? formatFloatingPointNumber()(v) : ""),
  })

  const _value = numeric_rifm.value
  const _onChange = numeric_rifm.onChange

  const disabled = !hasAuthority(["AUTH_EDIT_TAX"]) || status !== "DRAFT"
  const isPercentage = row.get()?.amountType === "PERCENTAGE"
  return !disabled ? (
    <>
      {isPercentage && _value !== "" && (
        <span
          className={cx("BodyBody-2BlackRegular", styles.percentage)}
        >{`${_value}%`}</span>
      )}
      <TextInput
        // error={!!error}
        // errorMessage={error}
        value={_value}
        onChange={_onChange}
        onBlur={onBlur}
        type="tel"
        className={cx(
          styles.input,
          styles.ghostSelect,
          isPercentage && styles.percentageInput,
          {
            [styles.error]: !!error,
          },
        )}
        InputProps={{}}
      />
    </>
  ) : (
    <div className={cx("BodyBody-2BlackRegular", styles.readonly)}>
      {_value}
      {isPercentage && _value !== "" ? "%" : ""}
    </div>
  )
}

const EditableCell = (props: CellRendererProps) => {
  const { dataKey, rowId } = props
  const { onUpdate } = useContextOptions()
  const { hasAuthority } = useAuthenticated()
  const [value, setValue, error] = useCell<Tax>(rowId, dataKey as keyof Tax, "")
  const row = useRow<Tax>(rowId)
  let old = useRef(value)
  const { status } = row.value ?? {}
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
  }
  const onBlur = () => {
    const hasValueDiff = old.current !== value
    if (dataKey !== undefined && hasValueDiff) {
      onUpdate(row.value, dataKey, value).then(() => (old.current = value))
    }
  }

  const disabled = !hasAuthority(["AUTH_EDIT_TAX"]) || status !== "DRAFT"

  return (
    <GhostTextInput
      // error={!!error}
      // errorMessage={error}
      value={value as string}
      onChange={onChange}
      onBlur={onBlur}
      className={cx(styles.input, styles.ghostInput, {
        [styles.error]: !!error,
      })}
      disabled={disabled}
    />
  )
}

const StatusCell = (props: CellRendererProps) => {
  const { dataKey, rowId } = props
  const [value] = useCell<Tax>(rowId, dataKey as keyof Tax, "")
  const { t } = useI18n()

  return value && typeof value === "string" ? (
    <Status className={styles.status} color={ColorMap[value]}>
      {t(`status.${value ?? ""}`)}
    </Status>
  ) : null
}

const ReadOnlyCell = (props: CellRendererProps) => {
  const { dataKey, rowId } = props
  const [value] = useCell<Tax>(rowId, dataKey as keyof Tax, "")
  const { t } = useI18n()

  return (
    <div className={cx("BodyBody-2BlackRegular", styles.readonly)}>
      {t(value as string)}
    </div>
  )
}

const EditableDateCell = (props: CellRendererProps) => {
  const { dataKey, rowId } = props
  const { meta } = useAuthenticated()
  const { timeZone = "Etc/UTC" } = meta ?? {}
  const { onUpdate } = useContextOptions()
  const { hasAuthority, dateFormatter } = useAuthenticated()

  const [value, setValue, error] = useCell<Tax>(rowId, dataKey as keyof Tax)

  const row = useRow<Tax>(rowId)

  const onChange = (date: Date | null) => {
    const utcDate = !!date ? zonedTimeToUtc(startOfDay(date), timeZone) : ""

    onUpdate(row.value, dataKey, new Date(utcDate)?.toISOString()).then(
      () => !!date && setValue(new Date(utcDate)?.toISOString()),
    )
  }
  const disabled = !hasAuthority(["AUTH_EDIT_TAX"])

  return !disabled ? (
    <DatePicker
      format="P"
      variant="inline"
      inputVariant="standard"
      disableToolbar={true}
      minDate={
        dataKey === "effectiveDateTo" ? row.value.effectiveDateFrom : undefined
      }
      maxDate={
        dataKey === "effectiveDateFrom" ? row.value.effectiveDateTo : undefined
      }
      allowKeyboardControl={true}
      fullWidth={true}
      invalidDateMessage={null}
      value={!!value ? (value as string) : null}
      className={styles.datePicker}
      onChange={onChange}
      autoOk
      minDateMessage={""}
      maxDateMessage={""}
      clearable={true}
      InputProps={{
        disableUnderline: true,
        classes: {
          input: cx({ [styles.error]: !!error }),
        },
      }}
    />
  ) : (
    <div className={cx("BodyBody-2BlackRegular", styles.readonly)}>
      {!!value ? dateFormatter(String(value)) : null}
    </div>
  )
}

export const SelectCell = (props: CellRendererProps) => {
  const { t } = useI18n()
  const { dataKey, rowId } = props
  const { onUpdate, taxesSemanticsOptions } = useContextOptions()
  const [value, setValue, error] = useCell<Tax>(rowId, dataKey as keyof Tax, "")
  const row = useRow<Tax>(rowId)
  const { hasAuthority } = useAuthenticated()
  const { status, invoiceScope } = row.value ?? {}

  const handleChange = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value } = evt.target
    if (value) {
      setValue(value)
    }
  }

  const onBlur = () => {
    if (dataKey !== undefined && !!value) {
      onUpdate(row.value, dataKey, value)
    }
  }

  let cellOptions = [] as any

  switch (dataKey) {
    case "semantic":
      const semanticsOptions = taxesSemanticsOptions?.map((semantic) => {
        return { label: semantic.name, value: semantic.value }
      })
      cellOptions = semanticsOptions || []
      break
    case "taxType":
      const taxTypeOptions = [
        { label: t("tax.taxType.NORMAL"), value: "NORMAL" },
        { label: t("tax.taxType.WITHHOLDING"), value: "WITHHOLDING" },
        { label: t("tax.taxType.EXEMPTION"), value: "EXEMPTION" },
      ]
      cellOptions = taxTypeOptions || []
      break
    case "invoiceScope":
      const invoiceScopeOptions = [
        { label: t("tax.invoiceScope.client"), value: "CLIENT" },
        { label: t("tax.invoiceScope.vendor"), value: "VENDOR" },
      ]
      cellOptions = invoiceScopeOptions || []
      break
    case "taxUsage":
      const taxUsageMC = { label: t("MC"), value: "MC" }
      const taxUsageAC = { label: t("AC"), value: "AC" }

      const taxUsageOptions =
        invoiceScope === "CLIENT" ? [taxUsageAC, taxUsageMC] : [taxUsageMC]

      cellOptions = taxUsageOptions || []
      break
    case "amountType":
      const typesOptions = [
        { label: t("tax.amount.PERCENTAGE"), value: "PERCENTAGE" },
      ]
      cellOptions = typesOptions || []
      break
    default:
      cellOptions = []
  }

  const disabled = !hasAuthority(["AUTH_EDIT_TAX"]) || status !== "DRAFT"

  return !disabled ? (
    <div className={styles.buttonContainer}>
      <Options
        name={dataKey || ""}
        value={value}
        options={cellOptions}
        onChange={handleChange}
        onBlur={onBlur}
        className={cx(styles.ghostSelect, {
          [styles.error]: !!error,
        })}
        size="medium"
        // error={error}
      />
    </div>
  ) : (
    <div className={cx("BodyBody-2BlackRegular", styles.readonly)}>
      {t(value as string)}
    </div>
  )
}

const styles = {
  readonly: css`
    margin: var(--spacing-m);
    text-transform: lowercase;
    :first-letter {
      text-transform: uppercase;
    }
  `,
  input: css`
    font-size: 13px;
    :hover {
      cursor: pointer;
    }
    .MuiOutlinedInput-input {
      font-size: 13px;
    }
  `,
  datePicker: css`
    padding: var(--spacing-m);
  `,
  ghostInput: css`
    :focus-within {
      border: 2px solid var(--primary-main, teal);
    }
  `,
  ghostSelect: css`
    align-items: center;
    border: none;

    outline: none;

    .MuiSvgIcon-root {
      display: none;
    }

    :not([data-disabled="true"]):hover {
      border-color: var(--primary-main, teal);
      .MuiSvgIcon-root {
        display: block;
      }
    }

    :focus,
    :focus-within {
      border-color: var(--primary-main, teal);
      .MuiSvgIcon-root {
        display: block;
      }
    }

    .MuiInputBase-root {
      font-size: 13px;
    }
    .MuiOutlinedInput-notchedOutline {
      border: none;
    }
    .MuiSelect-outlined.MuiSelect-outlined {
      padding-top: var(--spacing-xs);
      padding-bottom: var(--spacing-xs);
    }
  `,
  buttonContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  `,
  error: css`
    border: 1px solid var(--red);
  `,
  date: css`
    .MuiOutlinedInput-marginDense {
      height: 40px;
    }
    .MuiOutlinedInput-notchedOutline {
      border: none;
    }
    .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
      border: none;
    }
    width: auto;
  `,
  dateInput: css`
    > input {
      font-size: 13px;
      outline: none;
    }
  `,
  status: css`
    margin: var(--spacing-m);
  `,
  percentage: css`
    font-size: 13px;
    position: absolute;
    left: 16px;
    background-color: transparent;
  `,
  percentageInput: css`
    color: transparent;
    caret-color: #000;
    .MuiOutlinedInput-input {
      color: transparent;
      caret-color: #000;
    }
  `,
}

const commonColumns = () => [
  {
    id: "1",
    Header: "label",
    dataKey: "label",
    Cell: EditableCell,
  },
  {
    id: "2",
    Header: "semantics",
    dataKey: "semantic",
    Cell: SelectCell,
  },
  {
    id: "3",
    Header: "tax type",
    dataKey: "taxType",
    Cell: SelectCell,
  },
  {
    id: "4",
    Header: "Scope",
    dataKey: "invoiceScope",
    Cell: ReadOnlyCell,
  },
  {
    id: "5",
    Header: "ERP code",
    dataKey: "erpCode",
    Cell: EditableCell,
  },
  {
    id: "6",
    Header: "amount",
    dataKey: "amount",
    Cell: EditableNumberCell,
  },
  {
    id: "7",
    Header: "tax usage",
    dataKey: "taxUsage",
    Cell: SelectCell,
  },
  {
    id: "8",
    Header: "status",
    dataKey: "status",
    Cell: StatusCell,
  },
  {
    id: "9",
    Header: "Start Date",
    dataKey: "effectiveDateFrom",
    Cell: EditableDateCell,
  },
  {
    id: "10",
    Header: "End Date",
    dataKey: "effectiveDateTo",
    Cell: EditableDateCell,
  },
]

const required = [
  "label",
  "semantic",
  "taxType",
  "invoiceScope",
  "amount",
  "erpCode",
  "taxUsage",
]

const canActivateTax = (tax: Tax) =>
  required?.every((k) =>
    k !== "amount"
      ? Boolean(tax?.[k as keyof Tax])
      : !isNaN(Number(tax?.amount)),
  )

const actionColumn = [
  {
    id: "11",
    Header: "actions",
    dataKey: "",
    width: 80,
    Cell: function (props: CellRendererProps) {
      const { t } = useI18n()
      const { onDelete, onActivate, onDeactivate } = useContextOptions()
      const { rowId } = props
      const row = useRow<Tax>(rowId)
      const { hasAuthority } = useAuthenticated()
      const { id, status } = row.value ?? {}

      const hideActivate =
        !hasAuthority(["AUTH_EDIT_TAX"]) ||
        status === "ACTIVE" ||
        !canActivateTax(row.value)
      const hideDeactivate =
        !hasAuthority(["AUTH_EDIT_TAX"]) || status !== "ACTIVE"
      const hideDelete =
        !hasAuthority(["AUTH_DELETE_TAX"]) || status !== "DRAFT"

      return (
        <MoreActions
          options={[
            // ACTIVATE
            {
              hidden: hideActivate,
              label: t("taxes.list.activate.action"),
              icon: <CheckIcon />,
              onClick: () => {
                onActivate(id)
              },
            },
            // DEACTIVATE
            {
              hidden: hideDeactivate,
              label: t("taxes.list.deactivate.action"),
              icon: <Deactivate />,
              onClick: () => {
                onDeactivate(id)
              },
            },
            // DELETE
            {
              hidden: hideDelete,
              label: t("taxes.list.delete.action"),
              icon: <DeleteIcon />,
              onClick: () => {
                onDelete(id)
              },
            },
          ]}
        />
      )
    },
  },
]

export const COLUMNS = (actionAllowed: boolean) => {
  return actionAllowed
    ? [
        ...commonColumns().slice(0, 10),
        ...actionColumn,
        ...commonColumns().slice(10),
      ]
    : commonColumns()
}
